// @ts-strict-ignore
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button, Skeleton, Typography, useToast } from "@aviary";
import { fetchPostWithJson } from "@helpers/http";
import { ConfirmModal } from "@shared/components/ConfirmModal/ConfirmModal";
import { useStoreSharedQuery } from "@shared/data/queries/StoreQuery/Store.query.generated";
import { OAuthAuthorizationScope } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthAuthorizationStep/OAuthAuthorizationScope/OAuthAuthorizationScope";
import { useOauthAuthorizationsUnauthenticatedOAuthQuery as useOauthAuthorizationsQuery } from "@unauthenticated/oauth/data/queries/OauthAuthorizationsQuery/OauthAuthorizations.query.generated";
import { l } from "@unauthenticated/oauth/locales/i18n";

import { SkeletonOAuthAuthorizationStep } from "./SkeletonOAuthAuthorizationStep";

import * as styles from "./OAuthAuthorizationStep.styles";

interface Props {
  clientId: string;
  redirectUri: string;
  responseType: string;
  scopes: string;
  state: string;
  isFromSignIn: boolean;
  nonce: string;
  prompt: string;
  maxAge: string;
  skipConsent?: boolean;
}

const OAuthAuthorizationStep = ({
  clientId,
  redirectUri,
  responseType,
  scopes,
  state,
  isFromSignIn,
  nonce,
  prompt,
  maxAge,
  skipConsent = false,
}: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { data: storeData, loading: storeDataLoading } = useStoreSharedQuery({
    skip: isFromSignIn,
  });
  const { data, loading } = useOauthAuthorizationsQuery({
    variables: { clientId },
  });

  const handleOnAgree = async () => {
    try {
      const requestBodyWithOidc = {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType || "code",
        scope: scopes,
        state,
        ...(nonce && { nonce }),
        ...(prompt && { prompt }),
        ...(maxAge && { max_age: maxAge }),
      };

      const response = await fetchPostWithJson<{ status: string; redirect_uri: string }>(
        "/api/oauth/authorize",
        requestBodyWithOidc
      );
      if (response.status !== "redirect") {
        makeToast("error", t(l.oauth.unauthorized));
      } else {
        window.location.assign(response.redirect_uri);
      }
    } catch (error) {
      makeToast("error", error);
    }
  };

  useEffect(() => {
    if (skipConsent) {
      handleOnAgree();
    }
  }, [skipConsent]);

  if (loading) return <SkeletonOAuthAuthorizationStep />;

  const scopeDescriptions = data?.viewer?.oauthApplication?.scopeDescriptions ?? [];

  // Show loading state during auto-authorization
  if (skipConsent) {
    return (
      <div css={styles.main}>
        <Skeleton type="typography" typographySize="body" width={12} />
      </div>
    );
  }

  const renderContent = () =>
    scopeDescriptions.map(scopeData => (
      <OAuthAuthorizationScope key={scopeData.title} {...scopeData} />
    ));

  const renderConfirmModal = () => (
    <ConfirmModal
      headingText={t(l.oauth.AreYouSure)}
      bodyText={t(l.oauth.YourFullscriptAccountWill)}
      primaryButtonText={t(l.oauth.NoGoBack)}
      primaryOnClick={() => setIsModalOpen(false)}
      secondaryButtonText={t(l.oauth.YesCancelIntegration)}
      secondaryOnClick={() => window.location.assign(redirectUri)}
      secondaryButtonColor="system"
      isOpen={isModalOpen}
      hideClose
      onClose={() => setIsModalOpen(false)}
    />
  );

  const renderTitle = () => {
    if (isFromSignIn) return <Typography type="h2">{t(l.oauth.SignInComplete)}</Typography>;

    if (storeDataLoading) {
      return <Skeleton type="typography" typographySize="h2" width={8} margin="bottomLarge" />;
    }
    if (storeData?.currentStore.currentSetupPage === "complete") {
      return <Typography type="h2">{t(l.oauth.AccountSetupComplete)}</Typography>;
    }

    return <Typography type="h2">{t(l.oauth.JustOneMoreStep)}</Typography>;
  };

  return (
    <div css={styles.main}>
      <header css={styles.header}>
        {renderTitle()}
        <Typography css={styles.headerSubTitle}>{t(l.oauth.BeforeWeIntegrate)}</Typography>
      </header>
      <section>{renderContent()}</section>
      <section css={styles.actions}>
        <Button onClick={handleOnAgree}>{t(l.oauth.IAgree)}</Button>
      </section>
      {renderConfirmModal()}
    </div>
  );
};

export { OAuthAuthorizationStep };
